import React, { useEffect, useRef, useState } from 'react';
import Marquee from 'react-fast-marquee';
import './ScrollingSection.css';
import { prefersLiteEffects } from '../../utils/perf';

const useIntersectionObserver = (setIsVisible, { once = true } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { root: null, rootMargin: '0px 0px -120px 0px', threshold: 0.05 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [setIsVisible, once]);

  return ref;
};

const MarqueeVideo = ({ src, active }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    if (active) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }, [active, src]);

  if (!src) return <div className="marquee-video-fallback" aria-hidden="true" />;

  return (
    <video
      ref={videoRef}
      playsInline
      src={src}
      webkit-playsInline=""
      loop
      muted
      preload="none"
      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
    />
  );
};

const ScrollingSection = () => {
  const [isMarqueeVisible, setIsMarqueeVisible] = useState(false);
  const [isPhilosophyVisible, setIsPhilosophyVisible] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [useLite] = useState(() => prefersLiteEffects());
  const [videoSrcs, setVideoSrcs] = useState(null);
  const [globeSrc, setGlobeSrc] = useState(null);

  const marqueeRef = useIntersectionObserver(setIsMarqueeVisible);
  const philosophyRef = useIntersectionObserver(setIsPhilosophyVisible);
  const sectionRef = useRef(null);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { rootMargin: '200px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Defer media downloads until the section is near the viewport (desktop only).
  useEffect(() => {
    if (useLite || !isInView || videoSrcs) return undefined;
    let cancelled = false;

    Promise.all([
      import('../../assets/videos/loop1.mp4'),
      import('../../assets/videos/loop2.mp4'),
      import('../../assets/videos/loop4.mp4'),
      import('../../assets/images/globe.gif'),
    ]).then(([v1, v2, v3, globe]) => {
      if (cancelled) return;
      setVideoSrcs([v1.default, v2.default, v3.default]);
      setGlobeSrc(globe.default);
    });

    return () => {
      cancelled = true;
    };
  }, [useLite, isInView, videoSrcs]);

  const videosActive = isInView && !useLite && Boolean(videoSrcs);

  return (
    <div ref={sectionRef}>
      <div
        id="marquee"
        className={`marquee-container ${isMarqueeVisible ? 'visible' : ''}`}
      >
        <Marquee direction="left" ref={marqueeRef} play={isInView}>
          <h2><span className="fancy-font">CREATIVE</span><br />DESIGN</h2>
          <div className="marquee-video">
            {useLite ? (
              <div className="marquee-video-fallback" aria-hidden="true" />
            ) : (
              <MarqueeVideo src={videoSrcs?.[0]} active={videosActive} />
            )}
          </div>
          <h2><span className="fancy-font">CUSTOM</span><br />DEVELOPMENT</h2>
          <div className="marquee-video">
            {useLite ? (
              <div className="marquee-video-fallback" aria-hidden="true" />
            ) : (
              <MarqueeVideo src={videoSrcs?.[1]} active={videosActive} />
            )}
          </div>
          <h2><span className="fancy-font">BRAND</span><br />IDENTITIES</h2>
          <div className="marquee-video">
            {useLite ? (
              <div className="marquee-video-fallback" aria-hidden="true" />
            ) : (
              <MarqueeVideo src={videoSrcs?.[2]} active={videosActive} />
            )}
          </div>
        </Marquee>
        <div className={`line ${isPhilosophyVisible ? 'visible' : ''}`}></div>

        <div
          className={`philosophy ${isPhilosophyVisible ? 'visible' : ''}`}
          ref={philosophyRef}
        >
          <div className="philosophy-left">
            {useLite || !globeSrc ? (
              <div className="globe-fallback" aria-hidden="true" />
            ) : (
              <img src={globeSrc} alt="animated spinning globe" loading="lazy" decoding="async" />
            )}
          </div>
          <div className="philosophy-right">
            <h3>My Philosophy</h3>
            <p>
              The web doesn&apos;t need to be boring and I don&apos;t like cookie-cutter
              solutions. Embracing unique, user-centered design enhances usability, boosts
              engagement, and fosters innovation in web development.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrollingSection;
